// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';

import {ButtonTheme, ButtonType} from 'common/components/button/constants';
import {MPProperties} from 'common/constants';
import {emitTrackEvent} from 'common/util/dom';
import {FormattedProperty} from 'common/util/format-property';
import {TrackedEvents, TrackedProperties} from 'common/report/util/profile/tracking';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {IronContextRegistry} from 'common/report/context-registry';

import {UserSummaryPropertyVariables} from './types';
import {template} from './template';
import './index.styl';

interface ProfileUserSummaryState {
  showConfirmDeleteProfile: boolean;
}

export interface ProfileUserSummaryJsxProps {
  attrs: ProfileUserSummaryAttrs;
  on?: {
    deleteProfile?: (ev: CustomEvent) => void;
  };
}

export interface ProfileUserSummaryAttrs {
  aliases: string[];
  'aliases-loading': boolean;
  'data-group-id'?: string | number;
  'distinct-id': string;
  'aliases-error': boolean;
  'has-delete-permissions': boolean;
  'hide-delete'?: boolean;
  'profile-exists': boolean;
  'profile-summary': UserSummaryPropertyVariables;
  'project-id': number;
  'sidebar-mode'?: boolean;
  'user-properties': {[name: string]: FormattedProperty};
  'user-properties-loading': boolean;
  'workspace-id': number;
}

export class ProfileUserSummary extends Component<
  ProfileUserSummaryState,
  ProfileUserSummaryAttrs,
  unknown,
  unknown,
  IronContextRegistry
> {
  static get attrsSchema(): AttrsSchema<ProfileUserSummaryAttrs> {
    return {
      ...super.attrsSchema,
      aliases: `json`,
      'aliases-loading': `boolean`,
      'data-group-id': `string`,
      'distinct-id': `string`,
      'aliases-error': `boolean`,
      'has-delete-permissions': `boolean`,
      'hide-delete': `boolean`,
      'profile-exists': `boolean`,
      'profile-summary': `json`,
      'project-id': `number`,
      'sidebar-mode': `boolean`,
      'user-properties': `json`,
      'user-properties-loading': `boolean`,
      'workspace-id': `number`,
    };
  }

  get config() {
    return {
      template,
      defaultState: {
        showConfirmDeleteProfile: false,
      },
      contexts: [`i18n` as const],
      helpers: {
        hasUnsubscribed: (): boolean => Boolean(this.attr(`user-properties`)[MPProperties.Unsubscribed]),
        getDeleteProfileButtonAttrs: (): {buttonAttrs: any; tooltip: string} => {
          const {t} = this.getContext(`i18n`);
          const {'profile-exists': profileExists, 'has-delete-permissions': hasDeletePermissions} = this.attrs();

          let tooltip = t(`profile.delete`);

          if (!profileExists) {
            tooltip = t(`profile.delete-error-no-props`);
          } else if (!hasDeletePermissions) {
            tooltip = t(`profile.delete-error-no-perms`);
          }

          return {
            buttonAttrs: {
              theme: ButtonTheme.Tertiary,
              type: ButtonType.Destructive,
              disabled: !profileExists || !hasDeletePermissions,
            },
            tooltip,
          };
        },
        openDeleteProfileConfirmation: (): void => {
          this.update({
            showConfirmDeleteProfile: true,
          });
        },
        handleModalButton: (ev): void => {
          const {
            detail: {action},
          } = ev;
          if (action === `confirm`) {
            this.trackEvent(TrackedEvents.DeleteProfile);
            this.dispatchEvent(new CustomEvent(`deleteProfile`));
          }

          this.update({
            showConfirmDeleteProfile: false,
          });
        },
      },
    };
  }

  trackEvent(eventName: TrackedEvents, props: TrackedProperties = {}): void {
    emitTrackEvent(this, {eventName, props});
  }
}

export const ProfileUserSummaryEl = defineAndMakeJsxFactory<ProfileUserSummaryJsxProps>(
  `profile-user-summary`,
  ProfileUserSummary,
);
export const ProfileUserSummaryReact = panel2react<ProfileUserSummaryJsxProps>(
  `profile-user-summary`,
  ProfileUserSummary,
);
